import { Texture } from "@/core/backends/webgl/texture"
import { Adjustments } from "@/core/types"
import { computeImageState, createImageState } from "@/core/image-state"
import { uuid } from "@/utils/uuid"
import { vec2 } from "gl-matrix"
import { getDefaultAdjustments } from "@/core/adjustments"

export interface WhiteBalance {
	asShotTemperature: number
	asShotTint: number
}

type PhotoType = "raw" | "bitmap"

export class Photo {
	id: string
	type: PhotoType
	texture: Texture
	adjustments: Adjustments
	whiteBalance: WhiteBalance
	initialOrientation: number
	exposureBias: number
	isSourceMonochrome: boolean

	imageState = createImageState()

	constructor(options: {
		id?: string
		type: PhotoType
		texture: Texture
		whiteBalance?: WhiteBalance
		adjustments?: Adjustments
		orientation?: number
		exposureBias?: number
		originalImageSize?: vec2
		isSourceMonochrome?: boolean
	}) {
		this.id = options.id ?? uuid()
		this.type = options.type
		this.texture = options.texture
		this.whiteBalance = options.whiteBalance ?? {
			asShotTemperature: 5000,
			asShotTint: 0,
		}
		const defaultAdjustments = getDefaultAdjustments()

		this.adjustments = options.adjustments ?? {
			...defaultAdjustments,
		}
		this.initialOrientation = options.orientation ?? 0
		this.exposureBias = options.exposureBias ?? 0
		this.isSourceMonochrome = options.isSourceMonochrome ?? false

		this.imageState.originalImageSize =
			options.originalImageSize ?? this.imageState.originalImageSize

		this.computeState()
	}

	clone(options: { adjustments?: Adjustments }) {
		return new Photo({
			id: this.id,
			type: this.type,
			texture: this.texture,
			whiteBalance: this.whiteBalance,
			orientation: this.initialOrientation,
			exposureBias: this.exposureBias,
			originalImageSize: this.imageState.originalImageSize,
			isSourceMonochrome: this.isSourceMonochrome,
			adjustments: options.adjustments ?? this.adjustments,
		})
	}

	computeState() {
		computeImageState(this.texture, this.adjustments, this.initialOrientation, this.imageState)
	}
}
